<template>
  <div>
    <AwesomeLoader
      :visible.sync="loading"
      :fullscreen="false"
      :darkMode="false"
      :message="loaderMessage"
      :primaryColor="getSpinPrimaryColor"
      :secondaryColor="getSpinSecondaryColor"
    />
    <el-card class="register-card" v-if="pageType === 1 && !loading">
      <div class="logo-container">
        <img :src="getLogoInfo" alt="Kodefast Logo" class="logo" />
        <p class="sub-title">Create your account to get started as {{ getTenantName }}</p>
      </div>
      <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
        <div class="row">
          <el-form-item label="First Name" prop="firstName" class="half">
            <el-input v-model="form.firstName" placeholder="John" />
          </el-form-item>
          <el-form-item label="Last Name" prop="lastName" class="half">
            <el-input v-model="form.lastName" placeholder="Doe" />
          </el-form-item>
        </div>

        <el-form-item label="Email" prop="email">
          <el-input v-model="form.email" placeholder="you@company.com" />
        </el-form-item>

        <el-form-item label="Phone Number" prop="phone_number">
          <vue-tel-input
            v-model="form.phone_number"
            ref="phoneInput"
            :preferred-countries="['us', 'gb', 'in', 'au']"
            :enable-search="true"
            placeholder="Enter phone number"
            @input="onPhoneInput"
          />
        </el-form-item>

        <el-form-item label="Authentication Code" prop="tenantCode">
          <el-input v-model="form.tenantCode" placeholder="KF-XXXXX" />
        </el-form-item>

        <button class="submit-button !rounded-full" :loading="loading" @click="submitForm">
          Register {{ getTenantName }}
        </button>

        <p class="login-link">
          Already registered?
          <router-link to="/signin/tenant" class="fw-bold" :style="getPrimaryColor">
            LogIn
          </router-link>
        </p>
      </el-form>
    </el-card>

    <accessdenied v-else-if="pageType === 2 && !loading" :errorMessage="errorMessage" />
  </div>
</template>

<script>
  import axios from '@/config/axios';
  import app_config from '@/config/app';
  import CustomizationLayer from '@/mixins/CustomizationLayer';

  export default {
    name: 'TenantRegister',
    components: {
      accessdenied: () => import('./accessdenied.vue'),
      AwesomeLoader: () => import('./loader.vue'),
    },
    mixins: [CustomizationLayer],
    data() {
      return {
        loading: false,
        pageType: 0,
        loaderMessage: 'Loading .......!',
        errorMessage: '',
        defaultLogo: '../../assets/logo.webp',
        application_config: app_config,
        company_info: {},
        registration_config: {},
        isPhoneValid: false,
        form: {
          firstName: '',
          lastName: '',
          email: '',
          phone_number: '',
          tenantCode: '',
          phone_number_code: {},
        },
        rules: {
          firstName: [
            {
              required: true,
              message: 'First name is required',
              trigger: 'blur',
            },
            {
              min: 2,
              max: 50,
              message: 'First name must be between 2 and 50 characters',
              trigger: ['blur', 'change'],
            },
          ],
          lastName: [
            {
              required: true,
              message: 'Last name is required',
              trigger: 'blur',
            },
            {
              min: 2,
              max: 50,
              message: 'Last name must be between 2 and 50 characters',
              trigger: ['blur', 'change'],
            },
          ],
          email: [
            {
              required: true,
              message: 'Email is required',
              trigger: 'blur',
            },
            {
              type: 'email',
              message: 'Please enter a valid email address',
              trigger: ['blur', 'change'],
            },
          ],
          tenantCode: [
            {
              required: true,
              message: 'Tenant code is required',
              trigger: 'blur',
            },
            {
              pattern: /^KF-[A-Z0-9]{5,8}$/,
              message: 'Auth code must start with "KF-" followed by 5–8 alphanumeric characters',
              trigger: ['blur', 'change'],
            },
          ],
          phone_number: [
            {
              required: true,
              validator: (rule, value, callback) => {
                if (!value) {
                  callback(new Error('Phone number is required'));
                } else if (!this.isPhoneValid) {
                  callback(new Error('Please enter a valid phone number'));
                } else {
                  callback();
                }
              },
              trigger: ['blur', 'change'],
            },
          ],
        },
      };
    },
    created() {
      this.form.tenantCode = this.$route.params.unique_code || '';
    },
    async mounted() {
      if (this.form?.tenantCode) {
        await this.validateCode();
      } else {
        this.pageType = 1;
        this.loading = false;
        this.loaderMessage = 'Loading .....!';
      }
    },
    methods: {
      async validateCode() {
        this.loading = true;
        try {
          this.loaderMessage = 'Authenticating URL .....';
          const res = await axios.get(`/ap/verify/${this.form.tenantCode}`);
          this.registration_config = res.data.data;
          this.company_info = res.data.data.company_id;
          this.pageType = 1;
        } catch (err) {
          this.pageType = 2;
          this.errorMessage = err?.response?.data?.message || err.message;
        } finally {
          this.loading = false;
          this.loaderMessage = 'Loading .....!';
        }
      },
      onPhoneInput(e164Number, info) {
        const { number, country, nationalNumber, valid, countryCallingCode, formatted } = info;
        this.isPhoneValid = valid;
        const phoneNumber = formatted?.replace(`+${countryCallingCode} `, '');
        this.form.phone_number_code = {
          countryCallingCode: countryCallingCode,
          countryCode: country?.iso2?.toUpperCase() || '',
          isValid: valid,
          formatInternational: formatted || '',
          formattedNumber: phoneNumber || '',
          nationalNumber: nationalNumber || '',
          uri: 'tel:' + number,
          e164: number || '',
          phoneNumber: phoneNumber || '',
          formatNational: phoneNumber || '',
        };
        this.form.phone_number = number || '';
      },

      async submitForm() {
        this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;

          this.loading = true;
          this.loaderMessage = 'Verifying your data .....!';

          try {
            const res = await axios.post(`/ap/reg/${this.form.tenantCode}`, this.form);
            this.$message.success(`${this.getTenantName} registered successfully!`);
            this.$router.push({
              path: `/${this.form.tenantCode}/otp`,
              query: {
                id: res.data.contact._id,
                mail: res.data.contact.email,
              },
            });
            this.resetForm();
          } catch (err) {
            this.$message.error(err?.response?.data?.message || err.message);
          } finally {
            this.loading = false;
            this.loaderMessage = 'Loading .....!';
          }
        });
      },
      resetForm() {
        this.form = {
          firstName: '',
          lastName: '',
          email: '',
          phone_number: '',
          tenantCode: '',
          phone_number_code: {},
        };
        this.isPhoneValid = false;
      },
    },
  };
</script>

<style scoped>
  .vue-tel-input {
    height: 2.5em;
  }

  .register-card {
    width: 100%;
    border-radius: 12px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
  }

  .logo-container {
    text-align: center;
    margin-bottom: 18px;
  }

  .logo {
    width: 15em;
    border-radius: 1em;
    border: 1px solid #bbbbbb45;
    padding: 0.5em;
    margin: 0.5em;
  }

  .sub-title {
    font-size: 13px;
    color: #777;
    margin-top: 2px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  .half {
    flex: 1;
  }

  .submit-button {
    width: 100%;
    margin-top: 10px;
  }

  .login-link {
    text-align: center;
    margin-top: 10px;
    font-size: 13px;
    color: #666;
  }

  .login-link a {
    color: #409eff;
    text-decoration: none;
  }
</style>
